import { useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

import { previewPanel } from '../../constants';
import './styles.scss';
import { selectContent } from '../../store/CodePanel/selectors';
import { updateEventContent } from '../../store/EventPanel/actions';
import { setReload } from '../../store/PreviewPanel/actions';
import { selectReload } from '../../store/PreviewPanel/selectors';
import { selectDarkToggle } from '../../store/NavigationBar/selectors';
import { selectEmbeddingMainType } from '../../store/OperationsMenu/selectors';
import EmbeddingType from '../../utils/embeddingType';
// Component containing the Iframe of which the code from code editor will be executed in. Would show the preview/result
// of the executed code/embedding dashboard.
const PreviewPanel = ({
  content,
  reload,
  isDarkTheme,
  onUpdateEventContent,
  onSetReload,
  embeddingType,
}) => {
  const IFRAME_REF = useRef();
  const consoleListenerScript = `
  <script>
  window.console.log = function() {
    window.parent.console.log.apply(console, arguments)
  }
  window.console.error = function() {
    window.parent.console.error.apply(console, arguments)
  }
  window.onerror = function() {
    console.error.apply(console, arguments)
  }
  </script>
`;

  const getTargetValue = () => {
    if (embeddingType === EmbeddingType.Dashboard) {
      return 'dossier';
    }
    return 'embeddingContext';
  };

  const getEventTypeExpr = () => {
    return EmbeddingType.getFilterTapType(embeddingType) === EmbeddingType.Bot
      ? 'microstrategy.dossier.AgentEventType'
      : 'microstrategy.dossier.EventType';
  };

  const eventTypeExpr = getEventTypeExpr();

  const loadEventListenersScript = `
    try{
      function updateEventPanel(evt){
        let text = this.event.data.name;
        window.parent.postMessage([text, evt], '*');
      }

      function registerEvents(interval) {
        clearInterval(interval)
        const eventType = ${eventTypeExpr}
        let eventNames = Object.values(eventType)
        let eventKeys = Object.keys(eventType).map((key) => "${eventTypeExpr}." + key)
        for (const eventName of eventNames){
          ${getTargetValue()}.registerEventHandler(eventName, updateEventPanel)
        }
        console.log("Successfully registered event listeners for Event Handler:", eventKeys)
      }
      // Try to register the event listeners every .2 seconds and give up after 300 tries
      const attachEventListeners = () => {
        var count = 0;
        const interval = setInterval(() => {
          if (count++ >= 300) {
            // console.log("Failed to register event listeners for Event Handler: '${getTargetValue()}' was undefined")
            clearInterval(interval)
          }
          else if (typeof ${getTargetValue()} !== 'undefined' && ${getTargetValue()}){
            try {
              registerEvents(interval)
            } catch(error) {
              console.log("Failed to register event listeners for Event Handler:", error)
            }
          }
        }, 200)

      }
      attachEventListeners()
    } catch(error) {
      console.log("Failed to register event listeners:", error)
    }
  `;

  /**
   * Load the event listener script for dashboard object only when:
   *  1. the code panel has code
   *  2. the iframe is valid
   *  3. the example selected is a iframe dashboard example
   */
  const loadEventListeners = () => {
    if (!!content && content !== '' && IFRAME_REF.current && IFRAME_REF.current.contentWindow) {
      const script = IFRAME_REF.current.contentWindow.document.createElement('script');
      script.innerHTML = loadEventListenersScript;
      IFRAME_REF.current.contentWindow.document.body.appendChild(script);
    }
  };
  const srcDocContent = () => {
    if (content) {
      return consoleListenerScript + content;
    }
    return consoleListenerScript;
  };

  useEffect(() => {
    function receiveMessage(e) {
      const iframe = IFRAME_REF.current;
      if (iframe && iframe.contentWindow && e.source === iframe.contentWindow) {
        let text = `${e.data[0]} Event\n`;

        if (e.data[1]) {
          text += `${JSON.stringify(e.data[1], null, 2)}\n`;
        }
        text += '\n';
        onUpdateEventContent(text);
      }
    }
    window.addEventListener('message', receiveMessage);
    return () => {
      window.removeEventListener('message', receiveMessage);
    };
  });

  useEffect(() => {
    if (reload) {
      IFRAME_REF.current?.contentWindow?.location?.reload();
      onSetReload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `onSetReload` should not be passed into the dependencies array
  }, [reload]);

  return (
    <div className={isDarkTheme ? 'preview-panel dark' : 'preview-panel'}>
      <iframe
        className="embedding-iframe card-shadow"
        title={previewPanel.IFRAME_TITLE}
        id={previewPanel.IFRAME_ID}
        srcDoc={srcDocContent()}
        onLoad={() => loadEventListeners()}
        ref={IFRAME_REF}
        scrolling="yes"
      />
    </div>
  );
};

PreviewPanel.propTypes = {
  content: PropTypes.string.isRequired,
  reload: PropTypes.bool.isRequired,
  isDarkTheme: PropTypes.bool.isRequired,
  onUpdateEventContent: PropTypes.func.isRequired,
  onSetReload: PropTypes.func.isRequired,
  embeddingType: PropTypes.string.isRequired,
};

function mapStateToProps(state) {
  return {
    content: selectContent(state),
    reload: selectReload(state),
    isDarkTheme: selectDarkToggle(state),
    embeddingType: selectEmbeddingMainType(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onUpdateEventContent: updateEventContent,
      onSetReload: setReload,
    },
    dispatch,
  );
}
export default connect(mapStateToProps, mapDispatchToProps)(PreviewPanel);
